import React, {useCallback, useEffect, useMemo} from "react";
import {Box, CircularProgress, Grid2} from "@mui/material";
import * as yup from "yup";
import {useAuth} from "@/app/auth/AuthContext";
import {useNavigate} from "react-router";
import {Link, useSearchParams} from "react-router-dom";
import {AppPaths} from "@/app/AppPaths";
import {ExpiringSoonRequestDTO, SignInDTO} from "@/api/data-contracts";
import {useModelState} from "@/lib/modelstate";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {compactValue} from "@/lib/screen-class";
import {SNACKBAR_CLOSE_BUTTON_THRESHOLD_MS, useSnackbarEx} from "@/lib/snackbarex";
import {EMAIL_REGEX} from "@/regex";
import {useTranslation} from "react-i18next";
import {AuthI18N, UserI18N} from "@/translations";
import {isAxiosError} from "axios";
import {encodePasswordBase64} from "@/lib/utils";
import {useRecaptchaV3} from "@/lib/components/RecaptchaV3";
import {requestApi} from "@/boxhub-api";
import {
    buildExpiringRequestLabel,
    requestDeepLinkPath,
    shouldShowExpiringHints
} from "@/pages/login-page/expiring-hint";
import {useFeedbackDialog} from "@/pages/login-page/feedback/FeedbackDialogProvider";
import {resolveRedirectDestination} from "@/app/auth/signInRedirect";

/** Life duration for the expiring-soon hint snackbar — above the close-button threshold so it is dismissable. */
const EXPIRING_HINT_SNACKBAR_LIFE_MS = SNACKBAR_CLOSE_BUTTON_THRESHOLD_MS + 1000;

/** Upper bound on how many expiring-soon snackbars are shown after login, to avoid a banner flood. */
const EXPIRING_HINT_MAX_SNACKBARS = 5;

/** Stable style for the clickable deep-link element inside an expiring-soon snackbar. */
const EXPIRING_HINT_LINK_STYLE = {
    cursor: "pointer",
    textDecoration: "underline",
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    color: "inherit"
} as const;

interface ExpiringRequestHintSummaryProps {
    onNavigate: () => void;
    ariaLabel: string;
    linkText: string;
    hintText: string;
}

/**
 * Presentational helper that renders the summary ReactNode for a single expiring-soon snackbar.
 * Extracted as a stable module-level component to avoid inline JSX tree and style-object churn per forEach iteration.
 */
const ExpiringRequestHintSummary: React.FC<ExpiringRequestHintSummaryProps> = ({
    onNavigate,
    ariaLabel,
    linkText,
    hintText
}) => (
    <span>
        {hintText}{" "}
        <button type="button" aria-label={ariaLabel} onClick={onNavigate} style={EXPIRING_HINT_LINK_STYLE}>
            {linkText}
        </button>
    </span>
);

const UserAuthForm = () => {
    const { t } = useTranslation();
    const { login } = useAuth();
    const { triggerFeedbackCheck } = useFeedbackDialog();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectUrl = resolveRedirectDestination(searchParams.get("redirect"), AppPaths.EXTERNAL_REQUEST_OVERVIEW);
    const [busy, setBusy] = React.useState(false);
    const { showMessage } = useSnackbarEx();
    const [disabledTime, setDisabledTime] = React.useState<{ current: number; waitSeconds: number }>({
        current: 0,
        waitSeconds: 0
    });

    useEffect(() => {
        if (disabledTime.current === 0 && disabledTime.waitSeconds > 0) {
            setDisabledTime({ current: 0, waitSeconds: 0 });
            return;
        }
        if (disabledTime.current <= 0) {
            return;
        }
        const timerId = setTimeout(() => {
            setDisabledTime((old) => ({ ...old, current: Math.max(0, old.current - 2) }));
        }, 2000);
        return () => clearTimeout(timerId);
    }, [disabledTime]);

    const [recaptchaToken] = useRecaptchaV3({ action: "login" });

    const formSchema = useMemo(
        () =>
            yup.object().shape({
                email: yup
                    .string()
                        .required(t(UserI18N.form.validation.emailRequired))
                        .matches(EMAIL_REGEX, t(UserI18N.form.validation.emailInvalid)),
                password: yup.string().required(t(UserI18N.form.validation.passwordRequired)),
                recaptchaToken: yup.string().optional()
            }),
        [t]
    );

    const { model, errors, onChange, setErrors } = useModelState<SignInDTO>({
        schema: formSchema,
        initialState: { email: "", password: "" }
    });

    const formIsValid = useMemo(() => {
        try {
            formSchema.validateSync(model, { abortEarly: false });
            return true;
        } catch {
            return false;
        }
    }, [model, formSchema]);

    /**
     * Fetches the expiring-soon requests and shows one snackbar per request (capped at
     * EXPIRING_HINT_MAX_SNACKBARS). Fire-and-forget: this must never block or fail the login flow,
     * so all errors are swallowed and it is invoked AFTER navigation.
     */
    const showExpiringSoonHints = useCallback(async () => {
        try {
            const { data } = await requestApi.getExpiringSoon();
            if (!shouldShowExpiringHints(data)) {
                return;
            }
            data.slice(0, EXPIRING_HINT_MAX_SNACKBARS).forEach((entry: ExpiringSoonRequestDTO) => {
                const id = entry.id;
                if (id === undefined) {
                    return;
                }
                const label = buildExpiringRequestLabel(entry);
                const hintText = t(AuthI18N.login.message.expiringSoonRequestHint, {label});
                const linkText = t(AuthI18N.login.message.expiringSoonRequestHintLink);
                showMessage({
                    severity: "warning",
                    life: EXPIRING_HINT_SNACKBAR_LIFE_MS,
                    summary: (
                        <ExpiringRequestHintSummary
                            hintText={hintText}
                            linkText={linkText}
                            ariaLabel={hintText}
                            onNavigate={() => {
                                window.location.hash = requestDeepLinkPath(id);
                            }}
                        />
                    )
                });
            });
        } catch {
            // The expiring-soon hint is optional — errors must never block the login flow.
        }
    }, [t, showMessage]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (model.email !== null && model.password !== null) {
            try {
                setBusy(true);
                // Base64-encode the password after validation, UTF-8 safe.
                const encodedPassword = encodePasswordBase64(model.password);
                onChange({ name: "password", value: "" });
                const session = await login(model.email, encodedPassword, encodePasswordBase64(recaptchaToken));
                // Navigate immediately so the optional expiring-soon hint never delays login completion.
                navigate(redirectUrl);
                // Fire-and-forget: hints are shown via the global snackbar after navigation.
                void showExpiringSoonHints();
                // Fire-and-forget: the blocking feedback dialog (if any) is triggered only here —
                // exactly on the interactive form login, never on checkAuth/switchCompany. Pass the
                // freshly resolved session's roles explicitly — useAuth().roles would still be the
                // STALE pre-login value here, since AuthProvider has not re-rendered yet in this
                // same microtask continuation.
                void triggerFeedbackCheck(session.roles);
            } catch (error: unknown) {
                const axiosError = isAxiosError(error) ? error : null;
                const errorMessage = t(AuthI18N.login.message.loginFailedSummary);
                setErrors(
                    {
                        password: {
                            message: t(AuthI18N.login.message.loginFailed)
                        }
                    },
                    true
                );
                if (axiosError?.response?.status === 429) {
                    const retryAfter = axiosError.response.headers["retry-after"];
                    const waitSeconds = Number.parseInt(retryAfter, 10);
                    if (!Number.isNaN(waitSeconds)) {
                        setDisabledTime({ current: waitSeconds, waitSeconds });
                    }
                }
                showMessage({
                    severity: "error",
                    summary: errorMessage
                });
            } finally {
                setBusy(false);
            }
        }
    };

    const emailRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        setTimeout(() => {
            emailRef.current?.focus();
        }, 250);
    }, []);

    return (
        <Box component="form" onSubmit={handleSubmit} width="100%" padding={2}>
            <Grid2 container direction="column" spacing={3} width="100%">
                <Grid2 size={{ xs: 12 }}>
                    <StringInputField
                            label={t(UserI18N.form.field.email)}
                        name="email"
                        value={model.email}
                        onChange={onChange}
                        disabled={busy || disabledTime.current > 0}
                        error={errors.email}
                        autoComplete="username"
                        textFieldRef={emailRef}
                    />
                </Grid2>
                <Grid2>
                    <StringInputField
                            label={t(UserI18N.form.field.password)}
                        name="password"
                        value={model.password}
                        onChange={onChange}
                        type="password"
                        disabled={busy || disabledTime.current > 0}
                        error={errors.password}
                        autoComplete="current-password"
                    />
                </Grid2>
                <Grid2>
                    <ButtonEx
                        label={
                            (busy && <CircularProgress size={24} />) ||
                            (disabledTime.current > 0 &&
                                    t(AuthI18N.login.message.loginLocked, {seconds: disabledTime.current})) ||
                                t(AuthI18N.login.action.login)
                        }
                        type="submit"
                        fullWidth
                        disabled={busy || !formIsValid || disabledTime.current > 0}
                        sx={{ mt: 3, mb: 2, whiteSpace: compactValue("normal", "nowrap") }}
                    />
                </Grid2>
                <Grid2 container justifyContent="flex-end">
                    <Link
                        to={AppPaths.USER_PASSWORD_RESET_REQUEST}
                        style={{ textDecoration: "none", color: "#1976d2" }}
                    >
                        {t(AuthI18N.login.action.passwordForgotten)}
                    </Link>
                </Grid2>
            </Grid2>
        </Box>
    );
};
export default UserAuthForm;
